import { icon } from "./iconsKit";
import { motionOk } from "./kit";
import { portraitUrl, uiUrl } from "./portraits";
import { Biome } from "./chapters";
import { WaveChip, chipsHtml } from "./wavePreview";
import { wipeActive } from "./transition";
import { IntroFadeMs, IntroHold, introHoldMs } from "./introHold";

/** After the intro card's fade should have ended, how long before it is taken down anyway (ms). */
const IntroFadeGraceMs = 400;

/**
 * The HUD's announcements (hud.ts): the banner slot (the level-intro card, a wave's arrival, a boss or a warning), the
 * moment callout, and the tier-up ribbons. The guide asks it what owns the player's attention (busyReason).
 */
export class HudBanners {
  readonly banner: HTMLDivElement;
  private bannerTimer = 0;
  /** The moment callout (MULTI-KILL, SAVED!…): one reused node, restarted per callout. */
  readonly callout: HTMLDivElement;

  /** `plate` is the HUD's top plate: the ribbons float in its parent. */
  constructor(private readonly plate: HTMLElement) {
    this.banner = document.createElement("div");
    this.banner.className = "banner hidden";
    this.callout = document.createElement("div");
    this.callout.className = "moment-callout hidden";
    this.callout.style.pointerEvents = "none";
  }

  /** Boss arrival: portrait over a red ribbon and a one-liner; warnings are a small plate strip. */
  announce(text: string, kind: "boss" | "warn" = "boss", ms = 2200, sub = "", portraitId = ""): void {
    this.endIntro();
    const img = kind === "boss" && portraitId ? portraitUrl(portraitId) : null;
    this.banner.innerHTML = kind === "boss"
      ? `${img ? `<img class="portrait" decoding="sync" src="${img}" alt="">` : ""}<div class="ribbon disp red">${text}</div>${sub ? `<div class="plate">${sub}</div>` : ""}`
      : `<div class="plate warn">${icon("skull", 16)} ${text}</div>`;
    this.banner.className = `banner ${kind}`;
    window.clearTimeout(this.bannerTimer);
    this.bannerTimer = window.setTimeout(() => { this.banner.className = "banner hidden"; }, ms);
  }

  /** A wave's arrival: its number (0: an Iron run's one wave) over the composition, flagged when the boss comes with it; FINAL when `stage` is `stages`. */
  waveIncoming(stage: number, stages: number, chips: readonly WaveChip[], ms = 1700): void {
    this.endIntro();
    const boss = chips.some((c) => c.boss);
    this.banner.innerHTML = `<div class="ribbon disp${boss ? " red" : ""}">${stage === 0 ? "THE IRON WAVE" : `WAVE ${stage}`}${stage === stages ? " · FINAL" : ""}</div><div class="plate wchips">${chipsHtml(chips, 26)}</div>${boss ? `<div class="plate warn">${icon("skull", 16)} Boss wave</div>` : ""}`;
    this.banner.className = "banner wave";
    window.clearTimeout(this.bannerTimer);
    this.bannerTimer = window.setTimeout(() => { this.banner.className = "banner hidden"; }, ms);
  }

  /**
   * A moment's callout (level/moments.ts): a gold ribbon with an optional line under it that pops in and fades over
   * CalloutMs, over the middle of the map. It has its own node, so it never cuts off a wave or boss banner.
   */
  showCallout(title: string, sub = "", kind = ""): void {
    const c = this.callout;
    c.innerHTML = `<div class="ribbon disp">${title}</div>${sub ? `<div class="plate">${sub}</div>` : ""}`;
    c.className = `moment-callout ${kind}`;
    c.getAnimations?.().forEach((a) => a.cancel());
    if (typeof c.animate === "function") {
      const pop = motionOk()
        ? [{ opacity: 0, transform: "translate(-50%, -50%) scale(0.6)" }, { opacity: 1, transform: "translate(-50%, -50%) scale(1.12)", offset: 0.12 }, { opacity: 1, transform: "translate(-50%, -50%) scale(1)", offset: 0.22 }, { opacity: 1, transform: "translate(-50%, -50%) scale(1)", offset: 0.78 }, { opacity: 0, transform: "translate(-50%, -60%) scale(1)" }]
        : [{ opacity: 0 }, { opacity: 1, offset: 0.12 }, { opacity: 1, offset: 0.78 }, { opacity: 0 }];
      c.animate(pop, { duration: HudBanners.CalloutMs, easing: "ease-out", fill: "forwards" });
    }
    window.clearTimeout(this.calloutTimer);
    this.calloutTimer = window.setTimeout(() => { c.className = "moment-callout hidden"; }, HudBanners.CalloutMs);
  }
  private calloutTimer = 0;
  static readonly CalloutMs = 1500;

  /**
   * Level-intro card (drop 2 §3, mockup 09): chapter strip, ribbon, level name, one line, the enemies you will face.
   * It holds once it is really on screen, longer on a chapter's first level, then fades (timing in introHold.ts). It
   * never blocks: the card takes no touches, and a touch after a short minimum dismisses it while still reaching
   * whatever it landed on (a card of the opening hand). Resolves when the card is gone, or when another banner (a
   * wave, a boss) takes the slot.
   */
  intro(o: { chapter: number; chapterName: string; biome: Biome; level: number; copy: string; enemyIds: string[]; bossName: string | null; bossHint?: string; opensChapter: boolean; ribbon?: string; title?: string }): Promise<void> {
    this.endIntro();
    const foes = o.enemyIds.map((id) => portraitUrl(id)).filter((u): u is string => u !== null).map((u) => `<img decoding="sync" src="${u}" alt="">`).join("");
    this.banner.innerHTML = `<div class="panel intro">
        <img class="strip" decoding="sync" src="${uiUrl(`chapters/${o.biome}_intro@2x.jpg`)}" alt="">
        <div class="ribbon disp">${o.ribbon ?? `CHAPTER ${o.chapter} · ${o.chapterName.toUpperCase()}`}</div>
        <b class="disp lvl">${o.title ?? `Level ${o.level}`}</b><small>${o.copy}</small>
        <div class="foes">${foes}${o.bossName ? `<span class="plate disp gold">Boss · ${o.bossName}</span>` : ""}</div>
        ${o.bossHint ? `<small class="hint">${o.bossHint}</small>` : ""}
      </div>`;
    this.banner.className = "banner intro";
    window.clearTimeout(this.bannerTimer);
    return new Promise<void>((resolve) => {
      const clock = new IntroHold(performance.now());
      let raf = 0, fading: Animation | null = null;
      const onTouch = () => { if (clock.canSkip(performance.now())) fade(); };
      const finish = () => {
        cancelAnimationFrame(raf);
        window.clearTimeout(this.bannerTimer);
        document.removeEventListener("pointerdown", onTouch, true);
        fading?.cancel();
        this.introEnd = null;
        resolve();
      };
      const fade = () => {
        if (this.introEnd !== finish || fading) return;
        window.clearTimeout(this.bannerTimer);
        const hide = () => { if (this.introEnd === finish) { this.banner.className = "banner hidden"; finish(); } };
        if (!motionOk() || typeof this.banner.animate !== "function") { hide(); return; }
        fading = this.banner.animate([{ opacity: 1 }, { opacity: 0 }], { duration: IntroFadeMs, easing: "ease-in", fill: "forwards" });
        // Hidden however the fade ends: finished, cancelled from outside, or never ticked (a page frozen mid-fade). A
        // fade that was cancelled used to leave the card on screen for good, with no tap able to clear it (M30 #3).
        fading.finished.then(hide, hide);
        window.setTimeout(hide, IntroFadeMs + IntroFadeGraceMs);
      };
      const watch = () => {
        if (clock.frame(performance.now(), wipeActive())) this.bannerTimer = window.setTimeout(fade, introHoldMs(o.opensChapter));
        else raf = requestAnimationFrame(watch);
      };
      this.introEnd = finish;
      document.addEventListener("pointerdown", onTouch, { capture: true, passive: true });
      raf = requestAnimationFrame(watch);
    });
  }
  /** Stops an intro card in progress (its listeners and timers) because something else takes the banner slot. */
  endIntro(): void { this.introEnd?.(); }
  private introEnd: (() => void) | null = null;

  /** A gold ribbon that rises 20 px and fades over 0.9 s from a screen point (tier-ups, drop 2 §4). */
  floatRibbon(text: string, x: number, y: number): void {
    const r = document.createElement("div");
    r.className = "ribbon disp float";
    r.textContent = text;
    r.style.left = `${Math.round(x)}px`;
    r.style.top = `${Math.round(y)}px`;
    this.plate.parentElement?.append(r);
    window.setTimeout(() => r.remove(), 950);
  }

  /**
   * What owns the player's attention in the HUD now, for the guide (level/guideLevel.ts levelBusy): the intro card, a
   * wave / boss / warn banner, or a moment callout. Null when none is up.
   */
  get busyReason(): "intro" | "banner" | "callout" | null {
    if (this.introEnd !== null) return "intro";
    if (!this.banner.classList.contains("hidden")) return "banner";
    return this.callout.classList.contains("hidden") ? null : "callout";
  }

  dispose(): void { this.endIntro(); window.clearTimeout(this.bannerTimer); window.clearTimeout(this.calloutTimer); this.callout.remove(); this.banner.remove(); }
}
